import React from 'react';

import Button from 'CommonComponents/Button';

import ClipboardIcon from 'Assets/icons/clipboard-document-list.svg';
import CloudArrowUpIcon from 'Assets/icons/cloud-arrow-up.svg';
import FilterIcon from 'Assets/icons/filter.svg';
import MagnifyingGlassIcon from 'Assets/icons/magnifying-glass.svg';
import PlusIcon from 'Assets/icons/plus.svg';

// Shared empty state for the Project Plan grid, at every scope (programme, department, cost
// centre, venue, and the revenue faces that mount the same component). Replaces the bare
// "No data found" line so an empty plan reads as intentional and says what to do next.
//
// There are four variants rather than one message because a plan reaches zero activities in
// different ways, and the useful thing to say differs completely:
//
//   empty             — the plan genuinely has nothing in it. Offer the two ways to start one.
//   filtered          — a filter selection excludes every activity. The plan is fine; the view is
//                       narrow.
//   searched          — the search term matches nothing.
//   searched_filtered — search and filters together match nothing. Offer to clear either.
//
// Collapsing these into one "No plan yet" would tell a user with forty filter selections to go
// and create a plan they already have. The Milestone Roadmap's equivalent (RoadmapEmptyState)
// splits its copy for the same reason.
export const VARIANT_EMPTY = 'empty';
export const VARIANT_FILTERED = 'filtered';
export const VARIANT_SEARCHED = 'searched';
export const VARIANT_SEARCHED_FILTERED = 'searched_filtered';

const ICONS = {
    [VARIANT_EMPTY]: ClipboardIcon,
    [VARIANT_FILTERED]: FilterIcon,
    [VARIANT_SEARCHED]: MagnifyingGlassIcon,
    [VARIANT_SEARCHED_FILTERED]: FilterIcon,
};

const ProjectPlanEmptyState = ({ variant = VARIANT_EMPTY, searchTerm = '', canManage = false, onAddCategory, importUrl, onClearFilters, onClearSearch }) => {
    const Icon = ICONS[variant] || ICONS[VARIANT_EMPTY];

    const heading = {
        [VARIANT_EMPTY]: $t('No plan yet'),
        [VARIANT_FILTERED]: $t('No activities match your filters'),
        [VARIANT_SEARCHED]: $t('No results for "{{term}}"', { term: searchTerm }),
        [VARIANT_SEARCHED_FILTERED]: $t('No activities match your search and filters'),
    }[variant];

    const body = {
        [VARIANT_EMPTY]: $t('A plan is built from categories, then sub-categories and activities inside them. You can also upload one from a spreadsheet.'),
        [VARIANT_FILTERED]: $t('Every activity in this plan is currently filtered out. Widen the selection, or clear the filters to see the whole plan.'),
        [VARIANT_SEARCHED]: $t('No category, sub-category or activity matches that term. Check the spelling, or clear the search.'),
        [VARIANT_SEARCHED_FILTERED]: $t('Nothing in this plan matches both the search and the current filters. Clear either to widen the view.'),
    }[variant];

    return (
        // `tw-absolute tw-inset-0` fills the grid's frame, which is why the container it sits in
        // has to be positioned — see the note on GanttChart's chart container.
        //
        // role="status" so a screen reader is told the grid came back empty. Without it the only
        // signal is a silent swap of the grid for this block, which announces nothing.
        <div role="status" className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-6">
            <div className="tw-flex tw-items-center tw-justify-center tw-h-14 tw-w-14 tw-rounded-full tw-bg-background tw-mb-4">
                <Icon className="tw-h-6 tw-w-6 tw-text-charcoal-50" aria-hidden="true" />
            </div>
            <h4 className="tw-text-base tw-font-semibold tw-text-charcoal tw-mb-1">{heading}</h4>
            <p className="tw-text-sm tw-text-charcoal-75 tw-max-w-sm">{body}</p>

            {/* The actions a user can take about it. On the `empty` variant these are gated on
                canManage: a read-only user cannot add a category or upload a plan, and the
                toolbar above already hides both, so offering them here would be two dead
                buttons. Clearing a filter or a search is not gated — narrowing the view is the
                user's own doing and anyone may undo it. */}
            {variant === VARIANT_EMPTY && canManage && (
                <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2">
                    <Button variant="primary" onClick={onAddCategory}>
                        <PlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                        {$t('Add category')}
                    </Button>
                    {/* Absent on scopes that have no import route, exactly as the toolbar's
                        Upload button is. */}
                    {importUrl && (
                        <Button as="a" href={importUrl} variant="outline">
                            <CloudArrowUpIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />
                            {$t('Upload plan')}
                        </Button>
                    )}
                </div>
            )}

            {variant === VARIANT_FILTERED && (
                <Button variant="outline" className="tw-mt-4" onClick={onClearFilters}>
                    {$t('Clear filters')}
                </Button>
            )}

            {variant === VARIANT_SEARCHED && (
                <Button variant="outline" className="tw-mt-4" onClick={onClearSearch}>
                    {$t('Clear search')}
                </Button>
            )}

            {variant === VARIANT_SEARCHED_FILTERED && (
                <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2">
                    <Button variant="outline" onClick={onClearSearch}>
                        {$t('Clear search')}
                    </Button>
                    <Button variant="outline" onClick={onClearFilters}>
                        {$t('Clear filters')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ProjectPlanEmptyState;
